import { authService } from 'auth/services';

import {
  HYBRID_CONNECT_PRODUCT,
  SMART_HUB_2_PRODUCT,
  SMART_HUB_3_PRODUCT,
} from 'features/order-tracker/pages/order-summary/components/MilestonesColumn/constants';
import {
  buildStringFromParts,
  formatToDateWithWeekDay,
  getSafeUrl,
  isMaskedInformation,
  mapPhoneNumber,
  subtractMonthsFromDate,
} from 'features/order-tracker/utils';
import { DELIVERY_STATUSES, MILESTONE_STATUSES, ORDER_SERVICES_TYPES } from 'features/order-tracker/utils/entries';

import {
  CARRIER_SHORT_NAME_MAP,
  COURIER_TO_BE_CONFIRMED,
  DELIVERY_DETAILS_FAILED_SUBTITLE,
  DELIVERY_DETAILS_FAILED_TITLE,
  DELIVERY_TO_BE_SENT,
  ESTIMATED_DELIVERY_MESSAGE,
  ITEMS_AWAITING_DISPATCH,
  ITEM_AWAITING_DISPATCH,
  SECONDARY_CLASS_ID,
  TO_BE_CONFIRMED_ID,
  TO_BE_CONFIRMED_REFERENCE,
  TRACKING_DELIVERY_DETAILS_FAILED,
  MILESTONES_NAMES_MAPPER,
} from './constants';

export const getDeliveryDetailsFailedMilestone = (): common.OrderMilestone => ({
  id: TRACKING_DELIVERY_DETAILS_FAILED,
  milestoneStatus: MILESTONE_STATUSES.incomplete,
  title: DELIVERY_DETAILS_FAILED_TITLE,
  hasStatusLabel: false,
  subTitle: DELIVERY_DETAILS_FAILED_SUBTITLE,
  formattedDate: '',
  type: ORDER_SERVICES_TYPES.Delivery,
  status: TRACKING_DELIVERY_DETAILS_FAILED,
  displayTitle:MILESTONES_NAMES_MAPPER[DELIVERY_DETAILS_FAILED_TITLE] || DELIVERY_DETAILS_FAILED_TITLE,
});

const checkProductToTrackEvent = (dispatchDate: string, carrier: string, trackingReference: string): boolean =>
  Date.parse(dispatchDate) > subtractMonthsFromDate(4).getTime() &&
  !!carrier &&
  !!trackingReference &&
  trackingReference !== TO_BE_CONFIRMED_REFERENCE;

const getEarlyDate = (date1: string, date2: string): string => (Date.parse(date1) <= Date.parse(date2) ? date1 : date2);

const getFormattedDeliveryAddress = (address: dto.DeliveryAddress, isAuthenticated: boolean): string => {
  const {
    subBuildingName = '',
    postcode = '',
    thoroughfareName = '',
    buildingName = '',
    buildingNumber = '',
  } = address;

  if (!isAuthenticated) {
    return `Postcode: ${postcode}`;
  }

  const postCode = postcode && `, ${postcode}`;
  const formattedSubBuildingName = subBuildingName && `${subBuildingName}, `;

  return `${formattedSubBuildingName}${buildingName} ${buildingNumber} ${thoroughfareName}${postCode}`.replace(
    /\s{2}/,
    ' ',
  );
};

const getCarrierToShortName = (carrier: string): string => {
  const [courierName] = carrier.split(' ');

  const courierKey = [...Object.keys(CARRIER_SHORT_NAME_MAP)].find((key) => key.includes(courierName.trim()));

  return CARRIER_SHORT_NAME_MAP[courierKey || ''];
};

const getSiteContact = (contact: dto.DeliverySiteContact): string => {
  const { Title = '', FirstName = '', LastName = '', PreferredContact = '', Mobile, WorkPhone, HomePhone } = contact;

  const formalFullName = buildStringFromParts([Title, FirstName, LastName], ' ');
  let contactNumber;

  switch (PreferredContact) {
    case 'Work':
      contactNumber = WorkPhone;
      break;
    case 'Mobile':
      contactNumber = Mobile;
      break;
    case 'Home':
      contactNumber = HomePhone;
      break;
    default:
      contactNumber = '';
  }

  const formattedContactNumber = isMaskedInformation(contactNumber) ? contactNumber : mapPhoneNumber(contactNumber);

  return (
    (formalFullName && contactNumber && `${formalFullName}: ${formattedContactNumber}`) ||
    formalFullName ||
    formattedContactNumber
  );
};

const mapDeliveryMilestoneItems = (deliveryItems: common.DeliveryItem[]): common.OrderMilestone[] =>
  deliveryItems.map((delivery) => {
    const {
      trackingReference,
      dispatchDate,
      deliveryAddress,
      products,
      carrierShortName,
      estimatedDeliveryDate,
      trackEvents,
    } = delivery;

    if (!dispatchDate) {
      const itemsToBeSentTitle = `${products.length} ${
        products.length > 1 ? ITEMS_AWAITING_DISPATCH : ITEM_AWAITING_DISPATCH
      }`;

      return {
        id: ITEMS_AWAITING_DISPATCH,
        milestoneStatus: MILESTONE_STATUSES.incomplete,
        title: itemsToBeSentTitle,
        hasStatusLabel: false,
        subTitle: DELIVERY_TO_BE_SENT,
        formattedDate: '',
        type: ORDER_SERVICES_TYPES.Delivery,
        status: DELIVERY_STATUSES.ToBeSent,
        delivery,
        displayTitle: MILESTONES_NAMES_MAPPER[itemsToBeSentTitle] || itemsToBeSentTitle,
      };
    }

    const isDelivered = !trackEvents && !!carrierShortName && !!trackingReference;
    const title = carrierShortName ? `${carrierShortName} delivery` : COURIER_TO_BE_CONFIRMED;
    const status =
      (isDelivered && DELIVERY_STATUSES.Delivered) ||
      (trackEvents && DELIVERY_STATUSES.InProgress) ||
      DELIVERY_STATUSES.Dispatched;
    const milestoneStatus =
      (isDelivered && MILESTONE_STATUSES.complete) ||
      (trackEvents && MILESTONE_STATUSES.inProgress) ||
      MILESTONE_STATUSES.incomplete;

    return {
      id: trackingReference,
      milestoneStatus,
      title,
      hasStatusLabel: trackEvents,
      subTitle: deliveryAddress,
      formattedDate: formatToDateWithWeekDay(estimatedDeliveryDate),
      type: ORDER_SERVICES_TYPES.Delivery,
      status,
      delivery,
      displayTitle:title,
    };
  });

export const getDeliveriesDetails = (
  deliveries: dto.OrderDeliveryDTO[],
  orderIds: string[],
): common.DeliveryDetailData => {
  const isAuthenticated = authService.isAuthenticated();

  const formattedDeliveries = deliveries
    .filter(({ OrderItemReference }) => OrderItemReference)
    .reduce(
      (
        {
          totalDeliveryProducts,
          sentDeliveryProducts,
          deliveriesToTrack,
          isHybridConnectAvailable,
          smartHubProduct,
          deliveryGroups,
        },
        delivery,
      ) => {
        const {
          OrderItemReference,
          EquipmentName,
          TrackingReference,
          TrackingReferenceURL,
          Carrier,
          DeliveryAddress,
          EstimatedDeliveryDate,
          DispatchDate,
          SiteContact,
        } = delivery;
        const deliveryGroupId = (DispatchDate && (TrackingReference || SECONDARY_CLASS_ID)) || TO_BE_CONFIRMED_ID;
        const existingGroup = deliveryGroups[deliveryGroupId];
        const carrierShortName = getCarrierToShortName(Carrier);
        const trackEvents = checkProductToTrackEvent(DispatchDate, carrierShortName, TrackingReference);
        const deliveryTrackingInfo = {
          orderId: orderIds.find((orderId) => OrderItemReference.includes(orderId)),
          itemReference: OrderItemReference,
          trackingReference: TrackingReference,
          carrier: Carrier,
        };
        const updatedDeliveriesToTrack = {
          ...deliveriesToTrack,
          ...(trackEvents ? { [deliveryGroupId]: deliveryTrackingInfo } : {}),
        };

        const smartHubProductInfo =
          (EquipmentName.includes(SMART_HUB_3_PRODUCT) && SMART_HUB_3_PRODUCT) ||
          (EquipmentName.includes(SMART_HUB_2_PRODUCT) && SMART_HUB_2_PRODUCT) ||
          '';

        const isHybridConnectProduct = EquipmentName.includes(HYBRID_CONNECT_PRODUCT);

        if (existingGroup) {
          existingGroup.products.push(EquipmentName);
          existingGroup.estimatedDeliveryDate = getEarlyDate(
            existingGroup.estimatedDeliveryDate,
            EstimatedDeliveryDate,
          );
          existingGroup.trackEvents ||= trackEvents;

          return {
            totalDeliveryProducts: 1 + totalDeliveryProducts,
            sentDeliveryProducts: +!!DispatchDate + sentDeliveryProducts,
            isHybridConnectAvailable: isHybridConnectAvailable || isHybridConnectProduct,
            deliveriesToTrack: updatedDeliveriesToTrack,
            smartHubProduct: smartHubProduct || smartHubProductInfo,
            deliveryGroups: {
              ...deliveryGroups,
              [deliveryGroupId]: existingGroup,
            },
          };
        }

        return {
          totalDeliveryProducts: 1 + totalDeliveryProducts,
          sentDeliveryProducts: +!!DispatchDate + sentDeliveryProducts,
          isHybridConnectAvailable: isHybridConnectAvailable || isHybridConnectProduct,
          deliveriesToTrack: updatedDeliveriesToTrack,
          smartHubProduct: smartHubProduct || smartHubProductInfo,
          deliveryGroups: {
            ...deliveryGroups,
            [deliveryGroupId]: {
              deliveryAddress: getFormattedDeliveryAddress(DeliveryAddress, isAuthenticated),
              carrierName: Carrier,
              carrierShortName,
              deliveryEstimationInfo: ESTIMATED_DELIVERY_MESSAGE,
              dispatchDate: DispatchDate && formatToDateWithWeekDay(DispatchDate),
              trackingReference: TrackingReference,
              trackingReferenceURL: getSafeUrl(TrackingReferenceURL),
              products: [EquipmentName],
              siteContact: getSiteContact(SiteContact),
              estimatedDeliveryDate: EstimatedDeliveryDate,
              trackEvents,
            },
          },
        };
      },
      {
        totalDeliveryProducts: 0,
        sentDeliveryProducts: 0,
        isHybridConnectAvailable: false,
        smartHubProduct: '',
        deliveriesToTrack: {},
        deliveryGroups: {},
      },
    );

  return {
    totalDeliveryProducts: formattedDeliveries.totalDeliveryProducts,
    sentDeliveryProducts: formattedDeliveries.sentDeliveryProducts,
    isHybridConnectAvailable: formattedDeliveries.isHybridConnectAvailable,
    smartHubProduct: formattedDeliveries.smartHubProduct,
    deliveriesToTrack: Object.values(formattedDeliveries.deliveriesToTrack),
    orderMilestones: mapDeliveryMilestoneItems(Object.values(formattedDeliveries.deliveryGroups)),
  };
};
